import type { JSX } from 'react'

import { useTranslation } from '@molecule/app-react'

import type { Paragraph } from '../content-types.js'
import { InlineMarkdown } from './InlineMarkdown.js'

/**
 * One paragraph's commentary in the gutter: the section's summary first, then
 * the prompt that produced the paragraph — each shown ONCE, beside the
 * paragraph it belongs to (§4).
 *
 * The block is a real element in the DOM with real text, never a bare `title`
 * attribute and never a hover popover: a popover repeating what the block
 * already shows is the duplicated-prompt bug the spec names. The two kinds of
 * commentary are visibly different things — the summary a quiet labelled panel,
 * the prompt the person's own words on their own accent — so a reader never
 * mistakes one for the other.
 */
export function GutterBlock({
  id,
  summary,
  paragraph,
  showSummary,
  showPrompts,
}: {
  id: string
  summary: string
  /** The FIRST paragraph this block's prompt produced, if any. */
  paragraph?: Paragraph | undefined
  showSummary: boolean
  showPrompts: boolean
}): JSX.Element | null {
  const { t } = useTranslation()
  const prompt = paragraph?.origin === 'ai' ? paragraph.prompt : undefined
  const model = paragraph?.origin === 'ai' ? paragraph.model : undefined
  const summaryVisible = showSummary && Boolean(summary)
  const promptVisible = showPrompts && Boolean(prompt)
  if (!summaryVisible && !promptVisible) return null

  return (
    <div className="mol-gutter-block" data-mol-id={`gutter-block-${id}`}>
      {summaryVisible && (
        <div className="mol-summary" data-mol-id={`summary-${id}`}>
          <p className="mol-gutter-label">
            {t('post.summaryLabel', undefined, { defaultValue: 'Summary' })}
          </p>
          <p className="mol-summary-text">
            {/* A TL;DR is prose, but a model sometimes echoes a code span it
                saw; rendering it inline keeps the backticks out of the text. */}
            <InlineMarkdown text={summary} />
          </p>
        </div>
      )}
      {promptVisible && (
        <div className="mol-prompt" data-mol-id={`prompt-${id}`}>
          <p className="mol-gutter-label">
            {t('post.promptLabel', undefined, { defaultValue: 'Prompt' })}
            {model && <span className="mol-prompt-model"> · {model}</span>}
          </p>
          <p className="mol-prompt-text">{prompt}</p>
        </div>
      )}
    </div>
  )
}
